// Phase 25 — Port do AuthModal de leyro.es a chain.
//
// Diferenzas con Leyro:
//   • Sen i18n (textos hardcoded gl). Engadir i18next en futura iter.
//   • Sen IntentStep (rol de chain asígnase noutro fluxo; nin RENTER/OWNER
//     /BOTH aplican aquí — son FARMER/BUYER/VET/TRANSPORTISTA).
//   • Sen campo phone (Phase 24 register endpoint non o admite aínda).
//   • TokenPair (non user) devolto polo register, polo que o toast usa
//     o nome enviado polo formulario.
//
// TODO Phase 25 iter-5 — Selección de rol no rexistro:
//   Cando o usuario crea conta hai que preguntar polos roles que quere
//   ter (FARMER · BUYER · VET · TRANSPORTISTA) e despois dirixilo ao
//   flow de KYC específico do rol primario:
//     - FARMER: validación REGA + acceso a Documenso TOS
//     - BUYER: DIDIT KYC (DNI + selfie)
//     - VET: DIDIT KYC + verificación colexiado (número + provincia)
//     - TRANSPORTISTA: DIDIT KYC + permiso de transporte de animais
//   Iso require ampliar o payload de POST /api/v1/auth/register para
//   aceptar `roles: UserRole[]` e crear os `UserRoleAssignment`
//   correspondentes no backend (xa hai modelo Prisma desde Phase 12).

import {
  AlertCircle,
  ArrowLeftRight,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Search,
  Sprout,
  Stethoscope,
  Truck,
  User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import type { ChangeEvent, FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { describeAxiosError } from '../../api/errors';
import { useAuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

import { GoogleSignInButton } from './GoogleSignInButton';
import { Turnstile } from './Turnstile';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';

// ─────────────────────────────────────────────────────────────────────────────
// InputField — copia visual exacta do Leyro
// ─────────────────────────────────────────────────────────────────────────────

interface InputFieldProps {
  icon: LucideIcon;
  type?: string;
  placeholder: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  rightSlot?: ReactNode;
  autoComplete?: string;
}

function InputField({
  icon: Icon,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  rightSlot,
  autoComplete,
}: InputFieldProps) {
  return (
    <div className="space-y-1">
      <div
        className={`relative flex items-center border rounded-xl px-4 py-3 transition-colors ${
          error ? 'border-rauxa' : 'border-border focus-within:border-dark'
        }`}
      >
        <Icon size={16} className="text-muted shrink-0 mr-3" />
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          className="flex-1 outline-none text-sm text-dark placeholder-muted bg-transparent"
        />
        {rightSlot && <div className="ml-2 shrink-0">{rightSlot}</div>}
      </div>
      {error && (
        <p className="flex items-center gap-1 text-xs text-rauxa pl-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LoginForm
// ─────────────────────────────────────────────────────────────────────────────

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const { login } = useAuthContext();
  const { addToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState('');
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = 'Introduce un email válido.';
    if (!password) e.password = 'Introduce o teu contrasinal.';
    return e;
  };

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setGlobalError('');
    setLoading(true);
    try {
      const tokens = await login(email.trim().toLowerCase(), password);
      addToast(t('auth.login.greet', { name: tokens.user.name || tokens.user.email }), 'success');
      onSuccess();
    } catch (err: unknown) {
      setGlobalError(describeAxiosError(err, 'Credenciais inválidas.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <InputField
        icon={Mail}
        type="email"
        placeholder={t('auth.fields.email')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
        autoComplete="email"
      />
      <InputField
        icon={Lock}
        type={showPw ? 'text' : 'password'}
        placeholder={t('auth.fields.password')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
        autoComplete="current-password"
        rightSlot={
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="text-muted hover:text-dark transition-colors"
            aria-label={showPw ? t('auth.fields.hidePassword') : t('auth.fields.showPassword')}
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
      />

      {globalError && (
        <p className="flex items-center gap-1.5 text-sm text-rauxa">
          <AlertCircle size={14} />
          {globalError}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-3 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? <Spinner size={18} className="mx-auto" /> : t('auth.login.submit')}
      </button>

      <p className="text-xs text-center text-muted">
        {t('auth.modal.forgot')}{' '}
        <Link
          to="/marketplace/forgot-password"
          className="text-rauxa font-semibold hover:underline"
          onClick={onSuccess}
        >
          {t('auth.modal.forgotCta')}
        </Link>
      </p>

      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-2 text-muted">{t('auth.modal.orSeparator')}</span>
        </div>
      </div>
      <GoogleSignInButtonAdapter onSuccess={onSuccess} />
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RegisterForm
// ─────────────────────────────────────────────────────────────────────────────

type SelfRole = 'FARMER' | 'BUYER' | 'VET' | 'TRANSPORTISTA';

interface RoleOption {
  id: SelfRole;
  title: string;
  description: string;
  icon: LucideIcon;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    id: 'FARMER',
    title: 'Gandeiro/a',
    description: 'Xestiono a miña explotación, rexistro eventos e vendo animais.',
    icon: Sprout,
  },
  {
    id: 'BUYER',
    title: 'Comprador/a',
    description: 'Busco animais para mercar no marketplace ou na subasta.',
    icon: Search,
  },
  {
    id: 'VET',
    title: 'Veterinario/a',
    description: 'Aplico actos sanitarios e asino eventos en explotacións.',
    icon: Stethoscope,
  },
  {
    id: 'TRANSPORTISTA',
    title: 'Transportista',
    description: 'Documento a recollida e entrega de animais.',
    icon: Truck,
  },
];

function RegisterForm({ onSuccess }: { onSuccess: () => void }) {
  const { register } = useAuthContext();
  const { addToast } = useToast();
  const [step, setStep] = useState<'form' | 'roles'>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [terms, setTerms] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  // Roles seleccionados; o primeiro é o primary. Por defecto baleiro
  // até o user marcar polo menos un no step "roles".
  const [selectedRoles, setSelectedRoles] = useState<SelfRole[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
  const captchaRequired = !!siteKey;

  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Introduce o teu nome.';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = 'Introduce un email válido.';
    if (!password || password.length < 8) e.password = 'Mínimo 8 caracteres.';
    if (!terms) e.terms = 'Tes que aceptar os Termos e a Política de Privacidade.';
    if (captchaRequired && !turnstileToken) e.captcha = 'Pasa a verificación anti-bot.';
    return e;
  };

  const handleFormNext = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const e = validateForm();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setStep('roles');
  };

  const toggleRole = (role: SelfRole) => {
    setSelectedRoles((cur) =>
      cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role],
    );
  };

  const submitRoles = async () => {
    if (selectedRoles.length === 0) {
      setErrors({ roles: 'Marca polo menos un rol para continuar.' });
      return;
    }
    setErrors({});
    setGlobalError('');
    setLoading(true);
    try {
      const payload: {
        email: string;
        name: string;
        password: string;
        language: 'gl' | 'es';
        turnstileToken?: string;
        roles: SelfRole[];
      } = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        language: 'gl',
        roles: selectedRoles,
      };
      if (turnstileToken) payload.turnstileToken = turnstileToken;
      await register(payload);
      setSubmittedEmail(payload.email);
    } catch (err: unknown) {
      setGlobalError(describeAxiosError(err, 'Non se puido crear a conta.'));
      // Force a fresh Turnstile token; Cloudflare invalidates after the first verify.
      setTurnstileToken('');
      setTurnstileNonce((n) => n + 1);
      // Volver ao form básico para que poida resolver outra vez o captcha.
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  // Confirmation screen — Leyro shows a dedicated welcome that here is
  // simplified: instruct the user to check email + offer "Pechar".
  if (submittedEmail) {
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-dark">Conta creada</h3>
        <p className="text-sm text-muted leading-relaxed">
          Enviamos un correo a <span className="font-semibold text-dark">{submittedEmail}</span>{' '}
          para que verifiques o teu enderezo. Comproba a túa bandexa de entrada (e a carpeta de
          spam) e fai clic no enlace.
        </p>
        <p className="text-xs text-muted">
          Mentres tanto, xa podes navegar polo marketplace coa sesión iniciada. Algunhas accións
          (vender un animal, asinar como veterinario, etc.) requirirán completar a verificación de
          identidade KYC para o rol correspondente.
        </p>
        <button
          type="button"
          onClick={() => {
            addToast('Sesión iniciada. Verifica o email cando podas.', 'success');
            onSuccess();
          }}
          className="btn-primary w-full py-3 mt-2"
        >
          Pechar
        </button>
      </div>
    );
  }

  // Step "roles" — multi-select de role(s) que vai ter o usuario.
  if (step === 'roles') {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-semibold text-dark">Como vas usar LeyroChain?</h3>
          <p className="text-sm text-muted mt-1">
            Marca un ou varios. Cada rol pode ter unha verificación específica (KYC). O primeiro que
            selecciones será o teu rol principal.
          </p>
        </div>

        <div className="space-y-2">
          {ROLE_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isSelected = selectedRoles.includes(opt.id);
            const order = selectedRoles.indexOf(opt.id) + 1;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggleRole(opt.id)}
                disabled={loading}
                className={`w-full flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors disabled:opacity-50 ${
                  isSelected
                    ? 'border-rauxa bg-rauxa/5'
                    : 'border-border hover:border-dark hover:bg-subtle/40'
                }`}
              >
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                    isSelected ? 'bg-rauxa text-white' : 'bg-subtle text-dark'
                  }`}
                >
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-dark flex items-center gap-2">
                    {opt.title}
                    {isSelected && order === 1 ? (
                      <span className="text-[10px] uppercase tracking-wide font-bold text-rauxa bg-rauxa/10 px-1.5 py-0.5 rounded-full">
                        Principal
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted leading-snug mt-0.5">{opt.description}</div>
                </div>
                <div
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                    isSelected ? 'bg-rauxa border-rauxa text-white' : 'border-border'
                  }`}
                  aria-hidden
                >
                  {isSelected ? '✓' : ''}
                </div>
              </button>
            );
          })}
        </div>

        {errors.roles && (
          <p className="flex items-center gap-1 text-xs text-rauxa pl-1">
            <AlertCircle size={12} />
            {errors.roles}
          </p>
        )}

        {globalError && (
          <p className="flex items-center gap-1.5 text-sm text-rauxa">
            <AlertCircle size={14} />
            {globalError}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setStep('form')}
            disabled={loading}
            className="btn-outline flex-1 inline-flex items-center justify-center gap-2"
          >
            <ArrowLeftRight size={14} />
            Volver
          </button>
          <button
            type="button"
            onClick={() => void submitRoles()}
            disabled={loading || selectedRoles.length === 0}
            className="btn-primary flex-1 disabled:opacity-60"
          >
            {loading ? <Spinner size={18} className="mx-auto" /> : 'Crear conta'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleFormNext} noValidate className="space-y-4">
      <InputField
        icon={User}
        placeholder="Nome completo"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={errors.name}
        autoComplete="name"
      />
      <InputField
        icon={Mail}
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
        autoComplete="email"
      />
      <InputField
        icon={Lock}
        type={showPw ? 'text' : 'password'}
        placeholder="Contrasinal (mín. 8 caracteres)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
        autoComplete="new-password"
        rightSlot={
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="text-muted hover:text-dark transition-colors"
            aria-label={showPw ? 'Ocultar contrasinal' : 'Amosar contrasinal'}
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
      />

      {/* Terms checkbox */}
      <div className="space-y-1">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-dark shrink-0"
          />
          <span className="text-sm text-muted leading-snug">
            Acepto os{' '}
            <a
              href="/marketplace/termos"
              className="underline text-dark hover:text-rauxa"
              target="_blank"
              rel="noreferrer"
            >
              Termos de uso
            </a>{' '}
            e a{' '}
            <a
              href="/marketplace/privacidade"
              className="underline text-dark hover:text-rauxa"
              target="_blank"
              rel="noreferrer"
            >
              Política de Privacidade
            </a>
            .
          </span>
        </label>
        {errors.terms && (
          <p className="flex items-center gap-1 text-xs text-rauxa pl-1">
            <AlertCircle size={12} />
            {errors.terms}
          </p>
        )}
      </div>

      {captchaRequired && (
        <div>
          <Turnstile
            key={turnstileNonce}
            siteKey={siteKey}
            onToken={(t) => {
              setTurnstileToken(t);
              setErrors((prev) => ({ ...prev, captcha: '' }));
            }}
            onError={() => setTurnstileToken('')}
          />
          {errors.captcha && (
            <p className="flex items-center gap-1 text-xs text-rauxa pl-1">
              <AlertCircle size={12} />
              {errors.captcha}
            </p>
          )}
        </div>
      )}

      {globalError && (
        <p className="flex items-center gap-1.5 text-sm text-rauxa">
          <AlertCircle size={14} />
          {globalError}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-3 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? <Spinner size={18} className="mx-auto" /> : 'Continuar'}
      </button>

      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-2 text-muted">ou</span>
        </div>
      </div>
      <GoogleSignInButtonAdapter onSuccess={onSuccess} />
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GoogleSignInButton adapter — wraps the Phase 24 button to hide-when-no-id
// and to plug into useAuthContext directly.
// ─────────────────────────────────────────────────────────────────────────────

function GoogleSignInButtonAdapter({ onSuccess }: { onSuccess: () => void }) {
  const { loginWithGoogle } = useAuthContext();
  const { addToast } = useToast();
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
  const handleIdToken = useCallback(
    async (idToken: string) => {
      try {
        const tokens = await loginWithGoogle(idToken, 'gl');
        addToast(`Benvido, ${tokens.user.name || tokens.user.email}`, 'success');
        onSuccess();
      } catch (err) {
        addToast(describeAxiosError(err, 'Non se puido iniciar sesión con Google.'), 'error');
      }
    },
    [loginWithGoogle, addToast, onSuccess],
  );
  if (!clientId) return null;
  return <GoogleSignInButton clientId={clientId} onIdToken={handleIdToken} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// AuthModal — main export
// ─────────────────────────────────────────────────────────────────────────────

export interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
}

export default function AuthModal({ open, onClose, initialMode = 'login' }: AuthModalProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);

  return (
    <Modal open={open} onClose={onClose} title="LeyroChain">
      <div className="px-6 pb-8 pt-6">
        <h2 className="text-2xl font-semibold text-dark mb-6 leading-tight">
          {mode === 'login' ? t('auth.modal.welcomeLogin') : t('auth.modal.welcomeRegister')}
        </h2>

        <div className="flex border border-border rounded-xl overflow-hidden mb-6">
          <button
            type="button"
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              mode === 'login' ? 'bg-dark text-white' : 'text-muted hover:bg-subtle'
            }`}
            onClick={() => setMode('login')}
          >
            {t('auth.modal.tabLogin')}
          </button>
          <button
            type="button"
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              mode === 'register' ? 'bg-dark text-white' : 'text-muted hover:bg-subtle'
            }`}
            onClick={() => setMode('register')}
          >
            {t('auth.modal.tabRegister')}
          </button>
        </div>

        {mode === 'login' ? (
          <LoginForm onSuccess={onClose} />
        ) : (
          <RegisterForm onSuccess={onClose} />
        )}
      </div>
    </Modal>
  );
}
